import { BaseExportService } from "./BaseExportService";
import { generateKeywordGapCSV } from "./generators/csvKeywordGapGenerator";
import { generatePositionTrackingCSV } from "./generators/csvPositionTrackingGenerator";
import { generateRecommendationsCSV } from "./generators/csvRecommendationsGenerator";
import { generateKeywordsCSV } from "./generators/csvKeywordGenerator";
import { generateGenericCSV } from "./generators/csvGenerator";

// Competitor letters to match what's shown in the table
const COMPETITOR_LETTERS = ['A', 'B', 'C', 'D', 'E'];

// Display competitor domain names shown in the UI and CSV
const UI_COMPETITORS = [
  "competitor-a.com",
  "competitor-b.com",
  "competitor-c.com",
  "competitor-d.com",
  "competitor-e.com"
];

// Internal competitor names from the mock data
const INTERNAL_COMPETITORS = [
  "competitor1.com",
  "competitor2.com",
  "competitor3.com",
  "competitor4.com",
  "competitor5.com"
];

// Map between internal names and display names
const COMPETITOR_MAP: Record<string, string> = {
  "competitor1.com": "competitor-a.com",
  "competitor2.com": "competitor-b.com",
  "competitor3.com": "competitor-c.com", 
  "competitor4.com": "competitor-d.com",
  "competitor5.com": "competitor-e.com"
};

// Interface matching the expected input for position tracking generator
interface CompetitorPosition {
  current?: number | null;
  previous?: number | null;
}

interface PositionTrackingItem {
  keyword: string;
  volume?: number | string;
  cpc?: number | string;
  yourPosition?: number | string;
  previousPosition?: number | string;
  movement?: number | string;
  competitors?: Record<string, CompetitorPosition>;
  [key: string]: unknown;
}

// Export data item interface - made more flexible to accept TrackedKeyword directly
export interface ExportDataItem {
  [key: string]: unknown;
  keyword?: string;
  competitors?: Record<string, unknown>;
}

export type ExportData = ExportDataItem[] | { data: ExportDataItem[] };

const EXPORT_FILENAME = "export.csv";

export class CSVExportService extends BaseExportService {
  public exportToCSV(exportData: ExportData): void {
    try {
      console.log("[CSVExportService] Starting CSV export");
      
      // Validate export data
      if (!exportData) {
        console.error("[CSVExportService] Export data is null or undefined");
        return;
      }
      
      // Check if exportData is an array or an object containing data
      const dataArray = Array.isArray(exportData) 
        ? exportData 
        : (exportData as { data: ExportDataItem[] }).data;
        
      if (!dataArray || dataArray.length === 0) {
        console.error("[CSVExportService] Export data is empty");
        return;
      }
      
      // Log the first item to debug structure
      const firstItem = dataArray[0];
      console.log("[CSVExportService] First item in export data:", 
        JSON.stringify(firstItem, null, 2));
      
      // Check for position tracking data specifically
      if (this.isPositionTrackingData(firstItem)) {
        console.log("[CSVExportService] Detected position tracking data");
        console.log("[CSVExportService] Competitors:", 
          Object.keys(firstItem.competitors || {}));
        
        // Check sample competitor structure
        if (firstItem.competitors) {
          const competitorKeys = Object.keys(firstItem.competitors);
          if (competitorKeys.length > 0) {
            const sampleCompetitor = firstItem.competitors[competitorKeys[0]];
            console.log("[CSVExportService] Sample competitor structure:", 
              JSON.stringify(sampleCompetitor, null, 2));
          } else {
            console.warn("[CSVExportService] Competitors object exists but has no keys");
          }
        } else {
          console.warn("[CSVExportService] Missing competitors object in position tracking data");
        }
      }
      
      // Generate CSV content based on data type
      const csvContent = this.generateCSVContent(dataArray);
      
      // Create and download the file
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement("a");
      link.href = url;
      link.download = EXPORT_FILENAME;
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
      
      console.log("[CSVExportService] CSV export completed successfully");
    } catch (error) {
      console.error("[CSVExportService] Error exporting to CSV:", error);
    }
  }
  
  /**
   * Generates CSV content based on export data type
   */
  private generateCSVContent(exportData: ExportDataItem[]): string {
    // Check which type of data we're dealing with
    const firstItem = exportData[0];
    
    console.log("[CSVExportService] Generating CSV content");
    console.log("[CSVExportService] First item in generate content:", 
      JSON.stringify(firstItem, null, 2));
    
    if (this.isPositionTrackingData(firstItem)) {
      console.log("[CSVExportService] Using position tracking generator");
      // Cast the data to the expected type for position tracking
      return generatePositionTrackingCSV({ 
        data: exportData as unknown as PositionTrackingItem[] 
      });
    } else if ("keyword" in firstItem && "difficulty" in firstItem && "volume" in firstItem && "intent" in firstItem) {
      // This is keyword suggestions/overview data
      console.log("[CSVExportService] Using keyword suggestions generator");
      return generateKeywordsCSV({ data: exportData });
    } else if ("keyword" in firstItem && "difficulty" in firstItem) {
      console.log("[CSVExportService] Using recommendations generator");
      return generateRecommendationsCSV({ data: exportData });
    } else if ("keyword" in firstItem && "searchVolume" in firstItem) {
      console.log("[CSVExportService] Using gap analysis generator");
      return generateKeywordGapCSV({ data: exportData });
    } else {
      console.warn("[CSVExportService] Unknown data type, falling back to generic generator");
      return generateGenericCSV(exportData);
    }
  }
  
  /**
   * Helper function to identify position tracking data - simplified to focus on competitors
   */
  private isPositionTrackingData(item: ExportDataItem): boolean {
    // The main indicator is the presence of a competitors object
    const hasCompetitors = item && typeof item.competitors === 'object';
    
    if (hasCompetitors) {
      console.log("[CSVExportService] Found competitors object:", 
        item.competitors ? Object.keys(item.competitors).join(", ") : "{}");
      
      // Also log competitor data structure to verify
      const competitorKeys = Object.keys(item.competitors || {});
      if (competitorKeys.length > 0) {
        const sampleKey = competitorKeys[0];
        console.log(`[CSVExportService] Sample competitor (${sampleKey}):`, 
          JSON.stringify(item.competitors?.[sampleKey], null, 2));
      }
    }
    
    return hasCompetitors;
  }
}
